import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GAME_STAMP_GRACE_MS,
  isGameSeed,
  isGameStreakMilestone,
  isPositiveGameStamp,
  type ConversationSeed,
  type GameActionRequest,
  type GameBand,
  type GameScore,
  type GameSeed,
} from '@orborix/shared-types';
import type { GameRoundLive, GameSolutionLive, VoiceMessage } from '@orborix/voice-client';
import { useSfx } from '../../../lib/sfx/index.js';
import { haptic } from '../../../lib/haptics.js';
import { directionForSeed } from '../controls/directionsData.js';
import {
  INITIAL_GAME_RUNS,
  advanceGameRuns,
  gameAttemptActionFor,
  gameRunScore,
  gameSummaryOf,
  runAttemptIndexes,
  selectGameStamps,
  selectGraceIndexes,
  type GameAttemptAction,
  type GameBubbleStamp,
  type GameRunsState,
  type GameSummary,
} from '../gameArena.js';
import {
  INITIAL_CHALLENGE_TAGS,
  advanceChallengeTags,
  selectHistoricalChallengeTags,
  type ChallengeTagsState,
  type GameChallengeTag,
} from '../gameChallenge.js';
import { createBrowserUuid } from '../../../lib/uuid.js';
import {
  NO_SOLUTION_ANSWERS,
  SOLUTION_ANSWER_DEADLINE_MS,
  advanceSolutionAnswers,
  solutionRequestView,
  type GameSolutionAnswers,
  type GameSolutionView,
  type HistoryGameSolutions,
  type RevealCardModel,
  type RevealRequest,
  type SolutionRequest,
} from '../gameSolutions.js';

/** The difficulty controls of the score pill (every game but describe-it). */
export type GameDifficultyAction = 'easier' | 'harder';

/** The one retry / skip control of the thread (newest attempt only). */
export interface GameAttemptControl {
  index: number;
  action: GameAttemptAction;
  /** The coach is generating, the session ended, or it was already used. */
  disabled: boolean;
  used: boolean;
}

/** «No sé» (plan 47 Round 2): on the coach bubble whose challenge is on the table. */
export interface GameDontKnowControl {
  /** The coach bubble that served the challenge (its block carries the button). */
  index: number;
  /** The coach is generating, the session ended, or it was already used. */
  disabled: boolean;
  used: boolean;
}

/** «Ver solución» of one attempt bubble (plan 47 Round 2). */
export interface GameAttemptSolution {
  /** What was asked so far; `null` = not asked (the button only). */
  view: GameSolutionView | null;
  /** The button is offered (not asked yet; a failed ask retries from its panel). */
  canAsk: boolean;
  /** The session ended: nothing can be asked. */
  disabled: boolean;
}

/** The brief «↓ Bajamos a B1» moment of the score pill (the band never climbs on its own). */
export interface GameBandMoment {
  band: GameBand;
  /** Changes per moment (remounts the animation). */
  key: number;
}

/**
 * The game mode arena of the conversation (plan 47): everything the shells and
 * the thread need to render the arena, derived from the live session.
 */
export interface GameArena {
  /** The active game seed (any of the 5), `null` outside a game. */
  seed: GameSeed | null;
  /** translate-it is running: stamps, score, challenge blocks, retry / skip, «No sé» / «Ver solución». */
  translateIt: boolean;
  /**
   * The colour of the arena tint: the active game's (`directionForSeed`),
   * kept after leaving so the tint fades out in its own colour. `null` until
   * a game is picked.
   */
  tint: string | null;
  /** Message index → stamp of every game attempt of the thread (live + seeded history). */
  stamps: ReadonlyMap<number, GameBubbleStamp>;
  /** The running translate-it game's score; `null` outside translate-it. */
  score: GameScore | null;
  /**
   * The session can stamp attempts: `false` once a `game.round` said the
   * session runs no meaning check (`meaningCheck: false`, e.g. no TypeSafe
   * key) — then no attempt is ever stamped, so the score pill keeps only the
   * round (no ✓ / streak). `true` until told otherwise.
   */
  scored: boolean;
  /**
   * The running game's current challenge (`game.round`), `null` until the
   * coach serves this game's first one.
   */
  round: GameRoundLive | null;
  /** Summary card of each finished translate-it game, keyed by the message index it follows. */
  summaries: ReadonlyMap<number, GameSummary>;
  attemptControl: GameAttemptControl | null;
  /** Bubbles whose correction chips are concealed while their retry is pending. */
  concealed: ReadonlySet<number>;
  runAttemptAction: (index: number) => void;
  revealAttempt: (index: number) => void;
  /** Más fácil / Más difícil; `null` = not offered for this game. */
  difficulty: {
    onPick: (action: GameDifficultyAction) => void;
    disabled: boolean;
    /** The last pick, for a short pressed feedback. */
    pending: GameDifficultyAction | null;
  } | null;
  /**
   * Coach bubble index → the challenge it served (Round 2): the bubble shows
   * it as a highlighted block. Live bubbles of translate-it games and the
   * seeded history.
   */
  challenges: ReadonlyMap<number, GameChallengeTag>;
  /** The challenge served by the LIVE coach bubble (still on screen), if known. */
  liveChallenge: GameChallengeTag | null;
  /** «No sé» on the challenge on the table, `null` when it is not offered. */
  dontKnow: GameDontKnowControl | null;
  onDontKnow: () => void;
  /** Learner bubble index → its «Ver solución» (live «Casi» / «No era eso», and the history's). */
  solutions: ReadonlyMap<number, GameAttemptSolution>;
  askSolution: (index: number) => void;
  /** Message index → the «No sé» cards that go right after it. */
  revealCards: ReadonlyMap<number, ReadonlyArray<RevealCardModel>>;
  /** The band of the running translate-it game's challenges (`null` = unknown). */
  band: GameBand | null;
  /** The band just moved down by itself (auto-down): a brief moment. */
  bandMoment: GameBandMoment | null;
  /**
   * The challenge on the table came after a run of «¡Perfecto!» first tries
   * (`suggestHarder` of `game.round`): the conversation points the learner at
   * «Más difícil» with a coach-mark (`ConversationCoachmarks`). `null` = no
   * suggestion now.
   */
  harderHint: GameHarderHint | null;
}

/** «Parece que este nivel te queda fácil»: the band the learner could leave. */
export interface GameHarderHint {
  band: GameBand;
}

/** The arena outside any game, with no game history (tests, fixtures). */
export const INERT_GAME_ARENA: GameArena = {
  seed: null,
  translateIt: false,
  tint: null,
  stamps: new Map(),
  score: null,
  scored: true,
  round: null,
  summaries: new Map(),
  attemptControl: null,
  concealed: new Set(),
  runAttemptAction: () => {},
  revealAttempt: () => {},
  difficulty: null,
  challenges: new Map(),
  liveChallenge: null,
  dontKnow: null,
  onDontKnow: () => {},
  solutions: new Map(),
  askSolution: () => {},
  revealCards: new Map(),
  band: null,
  bandMoment: null,
  harderHint: null,
};

/** How long the difficulty button shows its pressed state. */
const DIFFICULTY_FEEDBACK_MS = 2500;

/** How long the «↓ Bajamos a B1» moment stays in the score pill. */
export const BAND_MOMENT_MS = 2800;

const NO_SOLUTIONS: ReadonlyArray<GameSolutionLive> = [];

/**
 * Game mode arena state (plan 47 Phases 2 + 4). Tags the learner bubbles of
 * each translate-it game (`advanceGameRuns`), runs the grace timer of each
 * pending stamp, plays the stamp sounds once per attempt, and owns the
 * retry / skip / difficulty actions (`sendGameAction`). Pure derivations live
 * in `gameArena.ts`.
 *
 * Mounted ALWAYS by the controller (it returns inert values outside a game).
 */
/**
 * The server's anchor of an attempt bubble for «Ver solución»: its turn index
 * and, when it belongs to an earlier session (a resume restarted the
 * numbering — `previousSession`), that session's id. `key` tells attempts of
 * different sessions apart. `null` = no anchor yet.
 */
function attemptTurnOf(
  m: VoiceMessage,
  sessionId: string | null,
): { key: string; turnIndex: number; sessionId: string | null } | null {
  if (m.previousSession) {
    const { sessionId: previous, turnIndex } = m.previousSession;
    return turnIndex === null ? null : { key: `${previous}:${turnIndex}`, turnIndex, sessionId: previous };
  }
  if (m.turnIndex === undefined) return null;
  return { key: `${sessionId ?? 'current'}:${m.turnIndex}`, turnIndex: m.turnIndex, sessionId: null };
}

export function useGameArena(args: {
  messages: ReadonlyArray<VoiceMessage>;
  activeSeed: ConversationSeed | null;
  gameRound: GameRoundLive | null;
  /** The coach is generating a reply (thinking / speaking). */
  coachBusy: boolean;
  /** The session ended (server-side close, lesson end): the controls go inert. */
  ended: boolean;
  sendGameAction: (action: GameActionRequest) => void;
  /** `useVoiceSession().gameSolutions` (Round 2); none by default. */
  gameSolutions?: ReadonlyArray<GameSolutionLive>;
  /** A live coach bubble (streaming / still speaking) is on screen. */
  liveCoach?: boolean;
  /** The seeded history's solutions («Continuar»), placed on its bubbles. */
  historySolutions?: HistoryGameSolutions | null;
  /** `useVoiceSession().sessionId`: the session the live bubbles' turn indexes belong to. */
  sessionId?: string | null;
}): GameArena {
  const { messages, activeSeed, gameRound, coachBusy, ended, sendGameAction } = args;
  const gameSolutions = args.gameSolutions ?? NO_SOLUTIONS;
  const liveCoach = args.liveCoach ?? false;
  const historySolutions = args.historySolutions ?? null;
  const sessionId = args.sessionId ?? null;
  const seed: GameSeed | null = isGameSeed(activeSeed) ? activeSeed : null;
  const translateIt = seed === 'translate-it';
  const seedColor = seed ? (directionForSeed(seed)?.color ?? null) : null;
  const [tint, setTint] = useState<string | null>(seedColor);
  if (seedColor !== null && seedColor !== tint) setTint(seedColor);

  // Runs + tags, advanced during render (derive-from-previous): the bubble
  // that just appeared is tagged in the same render that shows it.
  const [runsState, setRunsState] = useState<GameRunsState>(INITIAL_GAME_RUNS);
  const runs = advanceGameRuns(runsState, {
    messages,
    playingTranslateIt: translateIt,
    ended,
    gameRound,
  });
  if (runs !== runsState) setRunsState(runs);

  // Grace timers: a pending stamp settles with what arrived after
  // GAME_STAMP_GRACE_MS from its bubble's appearance (or, for an untagged
  // bubble, from its verdict's arrival). Every bubble that can show
  // "judging" gets one (`selectGraceIndexes`).
  const [graceOver, setGraceOver] = useState<ReadonlySet<number>>(() => new Set());
  const timersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const graceIndexes = useMemo(
    () => selectGraceIndexes(messages, runs.tags),
    [messages, runs.tags],
  );
  useEffect(() => {
    // An index dropped = its bubble left the thread (a cancelled turn removes
    // the last learner bubble): its timer and its settled grace go with it, or
    // the NEXT bubble at the same index would inherit them — no "judging", a
    // half-known stamp at once (and an early timer firing on it).
    const dropped: number[] = [];
    for (const [index, timer] of timersRef.current) {
      if (graceIndexes.has(index)) continue;
      clearTimeout(timer);
      timersRef.current.delete(index);
      dropped.push(index);
    }
    if (dropped.length > 0) {
      setGraceOver((prev) => {
        if (!dropped.some((i) => prev.has(i))) return prev;
        const next = new Set(prev);
        for (const i of dropped) next.delete(i);
        return next;
      });
    }
    for (const index of graceIndexes) {
      if (timersRef.current.has(index)) continue;
      timersRef.current.set(
        index,
        setTimeout(() => {
          setGraceOver((prev) => new Set(prev).add(index));
        }, GAME_STAMP_GRACE_MS),
      );
    }
  }, [graceIndexes]);
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  const stamps = useMemo(
    () => selectGameStamps(messages, runs.tags, graceOver),
    [messages, runs.tags, graceOver],
  );

  const current = runs.current;
  const currentIndexes = useMemo(
    () => (current ? runAttemptIndexes(runs.tags, current.id) : []),
    [current, runs.tags],
  );
  // The running game's rounds: one that changed after the run started (a
  // round left from an earlier run of the same connection is not this game's).
  const accepted =
    current !== null &&
    gameRound !== null &&
    gameRound !== current.roundAtStart &&
    gameRound.seed === 'translate-it';
  const round = accepted && gameRound.challenge ? gameRound : null;

  // Round 2 — which coach bubble served which challenge (derived during
  // render, like the runs: the bubble that commits shows its block at once).
  const [tagsState, setTagsState] = useState<ChallengeTagsState>(INITIAL_CHALLENGE_TAGS);
  const challengeTags = advanceChallengeTags(tagsState, {
    messages,
    gameRound,
    accept: accepted,
    liveCoach,
  });
  if (challengeTags !== tagsState) setTagsState(challengeTags);
  const historicalTags = useMemo(() => selectHistoricalChallengeTags(messages), [messages]);
  const challenges = useMemo(() => {
    if (historicalTags.size === 0) return challengeTags.byIndex;
    const out = new Map(historicalTags);
    for (const [i, tag] of challengeTags.byIndex) out.set(i, tag);
    return out;
  }, [historicalTags, challengeTags.byIndex]);
  const liveChallenge = liveCoach ? challengeTags.pending : null;

  // Round 2 — the server's `game.solution` answers, kept by request id across
  // reconnects (derived during render, like the runs). Each «No sé» / «Ver
  // solución» sends an id of this hook (`nextRequestId`), echoed back; one
  // with no answer by its deadline reads as failed (`clock` re-renders then).
  const [answersState, setAnswersState] = useState<GameSolutionAnswers>(NO_SOLUTION_ANSWERS);
  const answers = advanceSolutionAnswers(answersState, gameSolutions);
  if (answers !== answersState) setAnswersState(answers);
  const [clock, setClock] = useState(() => Date.now());
  const requestIdRef = useRef<{ prefix: string; n: number } | null>(null);
  const nextRequestId = useCallback((): string => {
    requestIdRef.current ??= { prefix: createBrowserUuid().slice(0, 8), n: 0 };
    requestIdRef.current.n += 1;
    return `${requestIdRef.current.prefix}-${requestIdRef.current.n}`;
  }, []);

  // Round 2 — «No sé»: the learner's reveals with their answers. A refused
  // one is as if it never happened (no card, not counted).
  const [reveals, setReveals] = useState<ReadonlyArray<RevealRequest>>([]);
  const revealViews = useMemo(
    () => reveals.map((r) => ({ request: r, view: solutionRequestView(answers, r, clock) })),
    [reveals, answers, clock],
  );
  const liveReveals = useMemo(
    () => revealViews.filter((r) => r.view.status !== 'refused'),
    [revealViews],
  );
  const revealCards = useMemo(() => {
    const out = new Map<number, RevealCardModel[]>();
    for (const [after, cards] of historySolutions?.revealsAfter ?? []) out.set(after, [...cards]);
    for (const { request, view } of liveReveals) {
      const list = out.get(request.anchor) ?? [];
      list.push({ key: `reveal-live-${request.id}`, challenge: request.challenge, view });
      out.set(request.anchor, list);
    }
    return out;
  }, [liveReveals, historySolutions]);

  const score = useMemo(
    () =>
      current
        ? gameRunScore(
            currentIndexes,
            stamps,
            liveReveals.filter((r) => r.request.run === current.id).map((r) => r.request.anchor),
          )
        : null,
    [current, currentIndexes, stamps, liveReveals],
  );

  const summaries = useMemo(() => {
    const out = new Map<number, GameSummary>();
    for (const run of runs.ended) {
      const indexes = runAttemptIndexes(runs.tags, run.id).filter((i) => stamps.has(i));
      const runReveals = liveReveals
        .filter((r) => r.request.run === run.id)
        .map((r) => ({
          after: r.request.anchor,
          challenge: r.request.challenge,
          solution: r.view.status === 'ready' ? (r.view.solutions[0] ?? null) : null,
        }));
      // After the last RENDERED message of the thread when the game ended
      // (tool messages are not rendered).
      let after = Math.min(run.endLen, messages.length) - 1;
      while (after >= 0 && messages[after]?.who === 'tool') after -= 1;
      if ((indexes.length === 0 && runReveals.length === 0) || after < 0) continue;
      out.set(after, gameSummaryOf(messages, indexes, stamps, runReveals));
    }
    return out;
  }, [runs.ended, runs.tags, stamps, messages, liveReveals]);

  // Retry / skip: only on the newest attempt of the running game, and only
  // while it is the thread's newest learner bubble.
  const [used, setUsed] = useState<ReadonlyMap<number, GameAttemptAction>>(() => new Map());
  const [revealed, setRevealed] = useState<ReadonlySet<number>>(() => new Set());
  let attemptControl: GameAttemptControl | null = null;
  const newest = currentIndexes[currentIndexes.length - 1];
  if (newest !== undefined) {
    let lastUser = -1;
    messages.forEach((m, i) => {
      if (m.who === 'user') lastUser = i;
    });
    const m = messages[newest];
    const usedAction = used.get(newest);
    const action = usedAction ?? (m ? gameAttemptActionFor(m, stamps.get(newest)) : null);
    if (action && newest === lastUser) {
      attemptControl = {
        index: newest,
        action,
        used: usedAction !== undefined,
        disabled: usedAction !== undefined || coachBusy || ended,
      };
    }
  }

  // A retried attempt hides its chips (the answer would be read off the
  // screen) until tapped, or until a later attempt of the game is judged.
  const concealed = useMemo(() => {
    const out = new Set<number>();
    for (const [index, action] of used) {
      if (action !== 'retry' || revealed.has(index)) continue;
      const run = runs.tags.get(index);
      let judgedAfter = false;
      for (const [j, r] of runs.tags) {
        const s = stamps.get(j);
        if (r === run && j > index && s !== undefined && s !== 'judging') judgedAfter = true;
      }
      if (!judgedAfter) out.add(index);
    }
    return out;
  }, [used, revealed, runs.tags, stamps]);

  const controlRef = useRef(attemptControl);
  controlRef.current = attemptControl;
  const runAttemptAction = useCallback(
    (index: number): void => {
      const control = controlRef.current;
      if (!control || control.index !== index || control.disabled) return;
      setUsed((prev) => new Map(prev).set(index, control.action));
      sendGameAction(control.action);
    },
    [sendGameAction],
  );
  const revealAttempt = useCallback((index: number): void => {
    setRevealed((prev) => new Set(prev).add(index));
  }, []);

  // Más fácil / Más difícil: every game but describe-it (its words are dealt
  // by the app). The server drops an action that does not apply and answers
  // with no turn, so the press only gets a short pressed state.
  const [difficultyPending, setDifficultyPending] = useState<GameDifficultyAction | null>(null);
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current);
    },
    [],
  );
  // One press per pressed window: each press is a cue the server stacks (the
  // shift is cumulative), and the button only disables once the coach turns
  // busy — a round trip later — so a double tap used to move two notches.
  const pickDifficulty = useCallback(
    (action: GameDifficultyAction): void => {
      if (pendingTimerRef.current) return;
      sendGameAction(action);
      setDifficultyPending(action);
      pendingTimerRef.current = setTimeout(() => {
        pendingTimerRef.current = null;
        setDifficultyPending(null);
      }, DIFFICULTY_FEEDBACK_MS);
    },
    [sendGameAction],
  );
  const difficulty =
    seed !== null && seed !== 'describe-it'
      ? { onPick: pickDifficulty, disabled: coachBusy || ended, pending: difficultyPending }
      : null;

  // Round 2 — «No sé»: on the newest challenge of the running game, while
  // no attempt at it came after (a `not-attempt` turn — a question, chat —
  // does not count; a turn still judging does, the coach is busy anyway). Not
  // on a challenge still in the live bubble: it appears when the coach is done.
  let dontKnow: GameDontKnowControl | null = null;
  if (current && translateIt && challengeTags.pending === null) {
    let at = -1;
    for (const i of challengeTags.byIndex.keys()) if (i >= current.startLen && i > at) at = i;
    if (at !== -1) {
      const attempted = currentIndexes.some(
        (j) => j > at && messages[j]?.gameAttempt?.verdict !== 'not-attempt',
      );
      // A refused «No sé» (the server saw nothing to reveal there) hides the
      // button: it did not reveal anything, so it must not read «Vista», and
      // pressing it again would be refused again.
      const pressed = revealViews.filter((r) => r.request.challengeIndex === at);
      if (!attempted && !pressed.some((r) => r.view.status === 'refused')) {
        const used = pressed.length > 0;
        dontKnow = { index: at, used, disabled: used || coachBusy || ended };
      }
    }
  }
  let lastRendered = messages.length - 1;
  while (lastRendered >= 0 && messages[lastRendered]?.who === 'tool') lastRendered -= 1;
  const dontKnowNow = {
    control: dontKnow,
    tag: dontKnow ? challengeTags.byIndex.get(dontKnow.index) : undefined,
    run: current?.id,
    anchor: lastRendered,
  };
  const dontKnowRef = useRef(dontKnowNow);
  dontKnowRef.current = dontKnowNow;
  const onDontKnow = useCallback((): void => {
    const { control, tag, run, anchor } = dontKnowRef.current;
    if (!control || control.disabled || !tag || run === undefined) return;
    // A double tap before the next render must not send two reveals.
    dontKnowRef.current = { ...dontKnowRef.current, control: { ...control, disabled: true } };
    const id = nextRequestId();
    setReveals((prev) => [
      ...prev,
      {
        id,
        sentAt: Date.now(),
        run,
        anchor,
        challengeIndex: control.index,
        challenge: tag.challenge,
        round: tag.round,
      },
    ]);
    sendGameAction({ action: 'reveal', requestId: id });
  }, [sendGameAction, nextRequestId]);

  // Round 2 — «Ver solución»: on every live «Casi» / «No era eso» attempt (not
  // only the newest: it does not drive the game), keyed by the attempt's turn
  // index (the server's anchor). The history shows what was asked back then.
  const [solutionRequests, setSolutionRequests] = useState<ReadonlyMap<string, SolutionRequest>>(
    () => new Map(),
  );
  const solutions = useMemo(() => {
    const out = new Map<number, GameAttemptSolution>();
    messages.forEach((m, i) => {
      if (m.who !== 'user') return;
      if (m.historical) {
        const view =
          m.historicalTurn && historySolutions?.attempts.get(m.historicalTurn.turnIndex);
        if (view) out.set(i, { view, canAsk: false, disabled: true });
        return;
      }
      const attempt = attemptTurnOf(m, sessionId);
      if (attempt === null || m.gameAttempt === undefined) return;
      const request = solutionRequests.get(attempt.key);
      const stamp = stamps.get(i);
      if (!request && stamp !== 'close' && stamp !== 'miss') return;
      const view = request ? solutionRequestView(answers, request, clock) : null;
      if (view?.status === 'refused') return;
      // A failed ask retries from its own panel («Reintentar»), not the button.
      out.set(i, { view, canAsk: view === null, disabled: ended });
    });
    return out;
  }, [messages, stamps, solutionRequests, answers, clock, historySolutions, ended, sessionId]);
  const solutionsNow = { messages, answers, solutionRequests, ended, sessionId };
  const solutionsRef = useRef(solutionsNow);
  solutionsRef.current = solutionsNow;
  const askSolution = useCallback(
    (index: number): void => {
      const {
        messages: msgs,
        answers: known,
        solutionRequests: asked,
        ended: over,
        sessionId: current,
      } = solutionsRef.current;
      const message = msgs[index];
      const attempt = message ? attemptTurnOf(message, current) : null;
      if (over || attempt === null) return;
      // One request per attempt at a time: a double tap (or «Reintentar»
      // pressed twice) before the answer must not ask — and pay — twice.
      const pending = asked.get(attempt.key);
      const at = Date.now();
      if (pending && solutionRequestView(known, pending, at).status === 'loading') return;
      const request: SolutionRequest = { id: nextRequestId(), sentAt: at };
      const nextAsked = new Map(asked).set(attempt.key, request);
      solutionsRef.current = { ...solutionsRef.current, solutionRequests: nextAsked };
      setSolutionRequests(nextAsked);
      sendGameAction({
        action: 'solution',
        turnIndex: attempt.turnIndex,
        requestId: request.id,
        // An attempt from before a resume names the session that judged it.
        ...(attempt.sessionId !== null ? { sessionId: attempt.sessionId } : {}),
      });
    },
    [sendGameAction, nextRequestId],
  );

  // The next deadline of a request still waiting: re-render then, so it
  // reads as failed (its answer was lost) instead of loading forever.
  useEffect(() => {
    let next = Infinity;
    const waiting = (r: SolutionRequest): void => {
      if (answers.has(r.id)) return;
      const due = r.sentAt + SOLUTION_ANSWER_DEADLINE_MS;
      if (due > clock) next = Math.min(next, due);
    };
    reveals.forEach(waiting);
    solutionRequests.forEach(waiting);
    if (next === Infinity) return undefined;
    const t = setTimeout(() => setClock(Date.now()), Math.max(0, next - Date.now()));
    return () => clearTimeout(t);
  }, [reveals, solutionRequests, answers, clock]);

  // Round 2 — the band in the pill, and its moment when it moved by itself.
  const band = accepted ? gameRound.band : null;
  const bandMoment = useBandMoment(accepted ? gameRound : null);
  const harderHint = useMemo<GameHarderHint | null>(
    () =>
      accepted && gameRound.event === 'challenge' && gameRound.suggestHarder && gameRound.band
        ? { band: gameRound.band }
        : null,
    [accepted, gameRound],
  );

  useGameStampSounds(stamps, runs.tags, liveReveals);

  return {
    seed,
    translateIt,
    tint: seedColor ?? tint,
    stamps,
    score,
    scored: gameRound?.meaningCheck !== false,
    round,
    summaries,
    attemptControl,
    concealed,
    runAttemptAction,
    revealAttempt,
    difficulty,
    challenges,
    liveChallenge,
    dontKnow,
    onDontKnow,
    solutions,
    askSolution,
    revealCards,
    band,
    bandMoment,
    harderHint,
  };
}

/**
 * The «↓ Bajamos a B1» moment (plan 47 Round 2): set by a `challenge` round
 * whose band moved down by itself (`auto-down`; a `manual` shift is the
 * learner's own press — the pill just shows the new band), cleared after
 * {@link BAND_MOMENT_MS}. Silent: it is not a punishment. There is no up
 * moment — the band only climbs on the learner's press (`harderHint`).
 */
function useBandMoment(round: GameRoundLive | null): GameBandMoment | null {
  const [moment, setMoment] = useState<GameBandMoment | null>(null);
  const [seen, setSeen] = useState<GameRoundLive | null>(round);
  if (round !== seen) {
    setSeen(round);
    const shift = round?.event === 'challenge' ? round.bandShift : null;
    if (round?.band && shift === 'auto-down') {
      setMoment({ band: round.band, key: (moment?.key ?? 0) + 1 });
    }
  }
  useEffect(() => {
    if (!moment) return undefined;
    const t = setTimeout(() => setMoment(null), BAND_MOMENT_MS);
    return () => clearTimeout(t);
  }, [moment]);
  return moment;
}

/**
 * The stamp sounds (plan 47 § The stamp): `game-correct` on a perfect / good,
 * `game-streak` instead when it reaches 3 / 5 / 10 in a row, a soft
 * `game-miss` on a miss, nothing on a close. Once per attempt, when its stamp
 * first settles; only live game bubbles (the tagged ones). Several settling at
 * once (a grace expiring) sound only the newest. The positive ones also give a
 * haptic in the native app (`lib/haptics.ts`): a tick, a success pattern on a
 * streak milestone; never on a miss.
 */
function useGameStampSounds(
  stamps: ReadonlyMap<number, GameBubbleStamp>,
  tags: ReadonlyMap<number, number>,
  reveals: ReadonlyArray<{ request: Pick<RevealRequest, 'run' | 'anchor'> }>,
): void {
  const { play } = useSfx();
  const announcedRef = useRef(new Set<number>());
  // Read at the moment a stamp settles (not a trigger of its own).
  const revealsRef = useRef(reveals);
  revealsRef.current = reveals;
  useEffect(() => {
    // A bubble that left the thread (cancelled turn) frees its index: the
    // next attempt landing there must sound too.
    for (const index of announcedRef.current) {
      if (!tags.has(index)) announcedRef.current.delete(index);
    }
    let newest = -1;
    for (const [index, stamp] of stamps) {
      if (stamp === 'judging' || !tags.has(index) || announcedRef.current.has(index)) continue;
      announcedRef.current.add(index);
      newest = Math.max(newest, index);
    }
    if (newest === -1) return;
    const stamp = stamps.get(newest);
    if (stamp === 'miss') {
      play('game-miss');
      return;
    }
    if (stamp === undefined || stamp === 'judging' || !isPositiveGameStamp(stamp)) return;
    const run = tags.get(newest)!;
    const upTo = runAttemptIndexes(tags, run).filter((i) => i <= newest);
    // A «No sé» before this attempt ends the streak, as in the pill.
    const seenBefore = revealsRef.current
      .filter((r) => r.request.run === run && r.request.anchor < newest)
      .map((r) => r.request.anchor);
    const { streak } = gameRunScore(upTo, stamps, seenBefore);
    const milestone = isGameStreakMilestone(streak);
    play(milestone ? 'game-streak' : 'game-correct');
    haptic(milestone ? 'success' : 'tick');
  }, [stamps, tags, play]);
}
